/**
 * ModelSelector — pick model per message.
 *
 * Layer: C (UX/CLI)
 * Spec: §4.2.1 — Lager 1: Användarpreferens. Om vald → ModelRouter respekterar valet.
 *   §5.3.2 — Dropdown i headern, grupperat per provider.
 *
 * Shows "Auto" + available models grouped by provider.
 * Fetches configured keys from /settings/keys to know which providers are active.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { settings, type ProviderKeyInfo } from '../lib/api';

interface ModelOption {
  provider: string;
  model: string;
  label: string;
}

const MODEL_CATALOG: Record<string, ModelOption[]> = {
  anthropic: [
    { provider: 'anthropic', model: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { provider: 'anthropic', model: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
    { provider: 'anthropic', model: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  ],
  openai: [
    { provider: 'openai', model: 'gpt-4.1', label: 'GPT-4.1' },
    { provider: 'openai', model: 'o3', label: 'o3' },
    { provider: 'openai', model: 'o4-mini', label: 'o4-mini' },
    { provider: 'openai', model: 'gpt-4o-mini', label: 'GPT-4o mini' },
  ],
  google: [
    { provider: 'google', model: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { provider: 'google', model: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  ],
  perplexity: [
    { provider: 'perplexity', model: 'sonar-pro', label: 'Sonar Pro' },
    { provider: 'perplexity', model: 'sonar', label: 'Sonar' },
  ],
  ollama: [
    { provider: 'ollama', model: 'qwen3:32b', label: 'Qwen3 32B' },
    { provider: 'ollama', model: 'deepseek-r1:32b', label: 'DeepSeek R1 32B' },
  ],
};

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  perplexity: 'Perplexity',
  ollama: 'Lokal (Ollama)',
};

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: 'text-provider-anthropic',
  openai: 'text-provider-openai',
  google: 'text-provider-google',
  perplexity: 'text-provider-perplexity',
  ollama: 'text-provider-ollama',
};

interface ModelSelectorProps {
  value: string | null; // null = Auto
  onChange: (model: string | null, provider: string | null) => void;
}

export function ModelSelector({ value, onChange }: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [configuredProviders, setConfiguredProviders] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Fetch configured providers
  useEffect(() => {
    (async () => {
      try {
        const { keys } = await settings.keys.list();
        setConfiguredProviders(new Set(keys.map((k: ProviderKeyInfo) => k.provider)));
      } catch { /* ignore */ }
      finally { setLoaded(true); }
    })();
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  // Find current label
  const currentLabel = value
    ? Object.values(MODEL_CATALOG)
        .flat()
        .find((m) => m.model === value)?.label ?? value
    : 'Auto';

  const handleSelect = useCallback(
    (model: string | null, provider: string | null) => {
      onChange(model, provider);
      setOpen(false);
    },
    [onChange],
  );

  if (!loaded) {
    return (
      <div className="rounded-md bg-bg-tertiary px-3 py-1.5 font-mono text-[13px] text-text-muted">
        ...
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-md border border-border bg-bg-tertiary
          px-3 py-1.5 font-mono text-[13px] text-text-secondary
          transition-colors hover:bg-bg-hover hover:text-text-primary"
      >
        <span>{currentLabel}</span>
        <svg
          className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12"
          fill="currentColor"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" fill="none" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-lg border border-border
            bg-bg-secondary shadow-lg shadow-black/30"
        >
          {/* Auto option */}
          <button
            onClick={() => handleSelect(null, null)}
            className={`flex w-full items-center px-3 py-2 text-left text-xs transition-colors
              hover:bg-bg-hover ${value === null ? 'text-accent' : 'text-text-secondary'}`}
          >
            <span className="font-mono">Auto</span>
            <span className="ml-2 text-[12px] text-text-muted">ModelRouter väljer</span>
          </button>

          <div className="border-t border-border-subtle" />

          {/* Grouped by provider */}
          {Object.entries(MODEL_CATALOG).map(([provider, models]) => {
            const isConfigured = configuredProviders.has(provider);
            if (!isConfigured) return null;

            return (
              <div key={provider}>
                <div className={`px-3 pt-2 pb-1 text-[12px] font-medium ${PROVIDER_COLORS[provider] ?? 'text-text-muted'}`}>
                  {PROVIDER_LABELS[provider] ?? provider}
                </div>
                {models.map((m) => (
                  <button
                    key={m.model}
                    onClick={() => handleSelect(m.model, m.provider)}
                    className={`flex w-full items-center px-3 py-1.5 text-left text-xs transition-colors
                      hover:bg-bg-hover ${
                        value === m.model ? 'text-accent' : 'text-text-primary'
                      }`}
                  >
                    <span className="font-mono">{m.label}</span>
                  </button>
                ))}
              </div>
            );
          })}

          {/* Unconfigured providers hint */}
          {configuredProviders.size === 0 && (
            <div className="px-3 py-3 text-[12px] text-text-muted">
              Lägg till API-nycklar under Inställningar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
